import { JobStatus, Version } from '../types/project.ts';
import { isFullyCatalogueAssigned, isMarketMatchedVersion } from './lampMarketMatch.ts';

/**
 * Checks if a job has started.
 * In our system, if a job is null, it means it hasn't started yet.
 */
const isJobStarted = (job: { jobStatus: JobStatus } | null | undefined): boolean => {
  return !!job;
};

export const isJobInProgress = (job: { jobStatus: JobStatus } | null | undefined): boolean => {
  return job?.jobStatus === JobStatus.IN_PROGRESS || job?.jobStatus === JobStatus.SCHEDULED;
};

export const isJobCompleted = (job: { jobStatus: JobStatus } | null | undefined): boolean => {
  return job?.jobStatus === JobStatus.COMPLETED;
};

export const isJobFailed = (job: { jobStatus: JobStatus } | null | undefined): boolean => {
  return job?.jobStatus === JobStatus.FAILED;
};

// Specialized helpers for common version-based checks

export const isCalculationInProgress = (version: Version | null | undefined): boolean => {
  return isJobInProgress(version?.calculationJob);
};

export const isCalculationCompleted = (version: Version | null | undefined): boolean => {
  return isJobCompleted(version?.calculationJob);
};

export const isCalculationStarted = (version: Version | null | undefined): boolean => {
  return isJobStarted(version?.calculationJob);
};

export const isAutopositioningInProgress = (version: Version | null | undefined): boolean => {
  return isJobInProgress(version?.autopositioningJob);
};

/** Project version allows editing lamps, layers, and related workspace data. */
export const canModifyWorkspace = (version: Version | null | undefined): boolean => {
  return !isCalculationStarted(version) && !isAutopositioningInProgress(version);
};

const isMarketMatchGroupEditingBlocked = (version: Version | null | undefined): boolean => {
  if (!version) return true;
  return (
    isAutopositioningInProgress(version) ||
    isCalculationInProgress(version) ||
    isRenderBakingInProgress(version) ||
    isJobInProgress(version.lampSimilarityJob)
  );
};

/** Whether market-match group assignments can be edited in the luminaire popup. */
export const canEditMarketMatchGroup = (version: Version | null | undefined): boolean => {
  if (!version || isMarketMatchedVersion(version) || isMarketMatchGroupEditingBlocked(version)) {
    return false;
  }
  if (canModifyWorkspace(version)) return true;
  return isCalculationCompleted(version);
};

/** Whether post-optimization market-match group edits should be auto-persisted. */
export const canPersistMarketMatchGroupChanges = (version: Version | null | undefined): boolean => {
  if (!version || isMarketMatchedVersion(version) || isMarketMatchGroupEditingBlocked(version)) {
    return false;
  }
  return isCalculationCompleted(version);
};

export const isRenderBakingInProgress = (version: Version | null | undefined): boolean => {
  return isJobInProgress(version?.renderBakingJob);
};

export const isBakingJobCompleted = (version: Version | null | undefined): boolean => {
  return isJobCompleted(version?.renderBakingJob);
};

export const isBakingJobFailed = (version: Version | null | undefined): boolean => {
  return isJobFailed(version?.renderBakingJob);
};

interface CanExportPdfOptions {
  /** Per-user `frontend-pdf-plots` feature flag. */
  frontendPdfPlotsEnabled?: boolean;
}

/**
 * PDF export is available after ML optimization completes and backend report images
 * are generated. With the frontend-pdf-plots flag, export is also enabled for
 * catalogue-assigned versions (frontend measurement) and ML-optimized custom-lamp
 * versions while frontend plots are generated client-side.
 */
export const canExportPdf = (version: Version | null | undefined, options?: CanExportPdfOptions): boolean => {
  if (!version) return false;

  if (isCalculationCompleted(version) && isJobCompleted(version.reportImagesJob)) {
    return true;
  }

  if (options?.frontendPdfPlotsEnabled && (isFullyCatalogueAssigned(version) || isCalculationCompleted(version))) {
    return true;
  }

  return false;
};
